import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Banner } from "../ui/Banner";

type Props = {
  open: boolean;
  onClose: () => void;
};

type JobStatus = "pending" | "running" | "done" | "error";
type CatalogJob = {
  id: number;
  type: string;
  status: JobStatus;
  products_imported: number;
  error: string | null;
  operator: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
};

export function IncrementalSyncModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<number | null>(null);
  const [done, setDone] = useState<CatalogJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setJobId(null);
    setDone(null);
    setError(null);
  }, [open]);

  const running = jobId != null && !done && !error;

  const { data: job } = useQuery<CatalogJob>({
    queryKey: ["catalog-job", jobId],
    queryFn: async () => (await api.get(`/api/catalog/jobs/${jobId}`)).data,
    enabled: running,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "pending" || s === "running" ? 1500 : false;
    },
  });

  useEffect(() => {
    if (!job) return;
    if (job.status === "done") {
      setDone(job);
      qc.invalidateQueries({ queryKey: ["catalog-status"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    } else if (job.status === "error") {
      setError(job.error ?? "Sincronizzazione fallita");
      setJobId(null);
    }
  }, [job, qc]);

  const startMut = useMutation({
    mutationFn: async () =>
      (await api.post<CatalogJob>("/api/catalog/sync-incremental")).data,
    onSuccess: (data) => setJobId(data.id),
    onError: (e) => setError(errMsg(e)),
  });

  const busy = startMut.isPending || running;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Sincronizza catalogo"
      footer={
        done ? (
          <Button variant="secondary" block onClick={onClose}>
            Chiudi
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Annulla
            </Button>
            <Button
              variant="primary"
              style={{ flex: 1 }}
              disabled={busy}
              onClick={() => {
                setError(null);
                startMut.mutate();
              }}
            >
              <Icon name="sync" size={14} />
              {busy ? "Sincronizzazione…" : "Avvia sincronizzazione"}
            </Button>
          </>
        )
      }
    >
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="danger" icon="x">
            {error}
          </Banner>
        </div>
      )}

      {!done && !running && (
        <>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--tcg-ink)" }}>
            Aggiornamento <strong>additivo</strong> del catalogo da TikTok Shop: aggiunge i
            prodotti nuovi e aggiorna i dati di quelli esistenti.
          </p>
          <ul
            style={{
              marginTop: 12,
              paddingLeft: 18,
              fontSize: 12,
              lineHeight: 1.6,
              color: "var(--tcg-ink-soft)",
            }}
          >
            <li>Nessun prodotto viene cancellato</li>
            <li>Lo stock dei prodotti esistenti non viene toccato</li>
            <li>I prodotti manuali (SKU FGC-…) sono preservati</li>
            <li>Eventi storici (stock_events) mantenuti per audit</li>
          </ul>
        </>
      )}

      {running && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <Banner tone="warn" icon="clock">
            {job?.status === "running"
              ? "Import da TikTok Shop in corso…"
              : "Job in coda, avvio imminente…"}
          </Banner>
          <p style={{ marginTop: 10, fontSize: 11, color: "var(--tcg-ink-soft)" }}>
            L'operazione gira in background: la finestra si aggiorna da sola al termine.
          </p>
        </div>
      )}

      {done && (
        <Banner tone="success" icon="check">
          Catalogo sincronizzato · {done.products_imported} nuovi prodotti importati.
        </Banner>
      )}
    </Modal>
  );
}
